import { css } from '@emotion/react';

import {
  type CarouselPositions,
  type DotsColors,
} from './ScrollingCarousel.types';

export const carouselRoot = css`
  position: relative;
`;

export const carousel = (gap?: string) => css`
  display: flex;
  overflow-y: hidden;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ${gap && `gap: ${gap};`}

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const step = (itemWidth?: string) => css`
  scroll-snap-align: center;
  flex-basis: ${itemWidth || '100%'};
  flex-shrink: 0;
  flex-grow: 0;

  @media (min-width: 768px) {
    flex-basis: ${itemWidth || '100%'};
  }
`;

export const controls = (position: CarouselPositions) => css`
  ${(position === 'left-right' || position === 'top-right') &&
  `
    display: none;

    @media (min-width: 768px) {
      display: block;
    }
  `}
`;

export const iconWrapper = css`
  display: flex;
  width: var(--spacing-7);
  height: var(--spacing-7);
  border-radius: 50%;
  background: var(--color-base-white);
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-md);
`;

export const button = (position: CarouselPositions) => css`
  background: transparent;
  border-color: transparent;
  outline: none;
  cursor: pointer;
  z-index: 15;
  position: absolute;
  opacity: 0.9;
  transition: transform 0.3s ease, opacity 0.3s ease;
  padding: 0;
  border-width: 0;

  ${position === 'left-right' &&
  `
    top: calc(50% - (var(--spacing-12) / 2));
  `}

  ${position === 'bottom' &&
  `
    top: calc(100% + var(--spacing-4));

    @media (min-width: 768px) {
      top: calc(100% + var(--spacing-8));
    }
  `}

  ${position === 'top-right' &&
  `
    top: calc(-1 * var(--spacing-24));
  `}

  ${position === 'inner-bottom' &&
  `
    bottom: calc(-1 * var(--spacing-1));
  `}
`;

export const buttonLeft = (position: CarouselPositions) => css`
  ${button(position)}

  ${position === 'left-right' &&
  `
    left: var(--spacing-2);
  `}

  ${position === 'bottom' &&
  `
    left: calc(50% - var(--spacing-14));
  `}

  ${position === 'top-right' &&
  `
    right: 60px;
    padding: 0;
  `}

  ${position === 'inner-bottom' &&
  `
    left: calc(50% - var(--spacing-16));
  `}
`;

export const customButtonLeft = (position: CarouselPositions) => css`
  ${button(position)}

  ${position === 'left-right' &&
  `
    left: 0;
  `}

  ${position === 'bottom' &&
  `
    left: calc(50% - var(--spacing-14));
  `}

  ${position === 'top-right' &&
  `
    right: var(--spacing-10);
    padding: 0;
  `}

  ${position === 'inner-bottom' &&
  `
    left: calc(50% - var(--spacing-16));
  `}
`;

export const buttonRight = (position: CarouselPositions) => css`
  ${button(position)}

  ${position === 'left-right' &&
  `
    right: var(--spacing-2);
  `}

  ${position === 'bottom' &&
  `
    right: calc(50% - var(--spacing-14));
  `}

  ${position === 'top-right' &&
  `
    right: 0;
    padding: 0;
  `}

  ${position === 'inner-bottom' &&
  `
    right: calc(50% - var(--spacing-16));
  `}
`;

export const icon = css`
  width: var(--spacing-3);
  height: var(--spacing-3);
  color: var(--color-base-black);
`;

export const dots = css`
  position: absolute;
  bottom: var(--spacing-2);
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const dotsInner = css`
  display: flex;
  overflow: hidden;
`;

export const dot = (dotsColor: DotsColors) => css`
  position: relative;
  flex-shrink: 0;
  flex-grow: 0;
  transition: transform 500ms;
  background-color: transparent;
  border: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    height: 8px;
    width: 8px;
    border-radius: 50%;
    transition: opacity 500ms, transform 500ms;
    opacity: 0.75;
    transform: translate(-50%, -50%);

    ${dotsColor === 'light' &&
    `
      background-color: var(--color-base-white);
    `}

    ${dotsColor === 'dark' &&
    `
      background-color: var(--color-base-black);
    `}

    ${dotsColor === 'neutral' &&
    `
      background-color: var(--color-neutral-500);
    `}
  }
`;

export const dotDistance2 = css`
  &::after {
    transform: translate(-50%, -50%) scale(0.9);
  }
`;

export const dotDistance3 = css`
  &::after {
    transform: translate(-50%, -50%) scale(0.8);
  }
`;

export const dotDistanceGreaterThan3 = css`
  &::after {
    transform: translate(-50%, -50%) scale(0.7);
  }
`;

export const dotVisible = css`
  &::after {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.2);
  }
`;
